import { useState, useMemo } from 'react'
import {
  Table,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Checkbox,
  Empty,
  Collapse,
  Tag,
} from 'antd'
import { SearchOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons'
import type { TableColumnsType, TablePaginationConfig } from 'antd'
import { useCurrentStructure, usePeriods } from '@/hooks/useRefOtdFilials'
import { useFilials } from '@/hooks/useRefs'
import type { CurrentStructureItem, CodeOtdDuplicate } from '@/types/refOtdFilials'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

const { Text, Title } = Typography
const { Panel } = Collapse

export default function CurrentStructure() {
  // Filters
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [filialFilter, setFilialFilter] = useState<number | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [onlyWithoutFilial, setOnlyWithoutFilial] = useState(false)

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Debounced search filter
  const debouncedSearch = useDebouncedValue(searchFilter, 500)

  // Fetch data
  const { data: filialsData, isLoading: filialsLoading } = useFilials()
  const filials = filialsData || []

  const { data: periodsData } = usePeriods()
  const hasPeriods = (periodsData?.periods?.length || 0) > 0

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      fil_code: onlyWithoutFilial ? undefined : filialFilter,
      status: statusFilter,
      page,
      page_size: pageSize,
      without_filial: onlyWithoutFilial || undefined,
    }),
    [debouncedSearch, filialFilter, statusFilter, page, pageSize, onlyWithoutFilial]
  )

  const { data, isLoading } = useCurrentStructure(filters)

  // Build set of duplicate code_otd (first 12 chars) for highlighting
  const duplicateCodeOtdSet = useMemo(() => {
    if (!data?.duplicates) return new Set<string>()
    return new Set(data.duplicates.map((d) => d.code_otd_12))
  }, [data?.duplicates])

  // Handlers
  const handleResetFilters = () => {
    setSearchFilter('')
    setFilialFilter(undefined)
    setStatusFilter(undefined)
    setOnlyWithoutFilial(false)
    setPage(1)
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current || 1)
    setPageSize(pagination.pageSize || 50)
  }

  // Stats
  const stats = useMemo(() => {
    if (!data) return { total: 0, withoutFilial: 0, withoutFilialActive: 0, withoutFilialClosed: 0 }
    return {
      total: data.total,
      withoutFilial: data.without_filial_count,
      withoutFilialActive: data.without_filial_active,
      withoutFilialClosed: data.without_filial_closed,
    }
  }, [data])

  // Row class for duplicate highlighting
  const getRowClassName = (record: CurrentStructureItem) => {
    if (record.code_otd && record.status === 'Активно') {
      const code12 = record.code_otd.slice(0, 12)
      if (duplicateCodeOtdSet.has(code12)) {
        return 'row-duplicate'
      }
    }
    return ''
  }

  // Columns with sorting
  const columns: TableColumnsType<CurrentStructureItem> = [
    {
      title: 'ID отделения',
      dataIndex: 'id_otd',
      key: 'id_otd',
      width: 120,
      fixed: 'left',
      ellipsis: true,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => a.id_otd.localeCompare(b.id_otd),
    },
    {
      title: 'Код отделения',
      dataIndex: 'code_otd',
      key: 'code_otd',
      width: 140,
      ellipsis: true,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.code_otd || '').localeCompare(b.code_otd || ''),
      render: (code_otd: string | null, record: CurrentStructureItem) => {
        if (!code_otd) return <Text type="secondary">—</Text>
        const first12 = code_otd.slice(0, 12)
        const rest = code_otd.slice(12)
        const isDuplicate = record.status === 'Активно' && duplicateCodeOtdSet.has(first12)
        return (
          <span>
            <Text strong style={isDuplicate ? { color: '#fa8c16' } : undefined}>{first12}</Text>
            {rest && <Text type="secondary">{rest}</Text>}
          </span>
        )
      },
    },
    {
      title: 'Отделение',
      dataIndex: 'code_otd_name_full',
      key: 'code_otd_name_full',
      ellipsis: true,
      width: 250,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.code_otd_name_full || '').localeCompare(b.code_otd_name_full || ''),
    },
    {
      title: 'Код подразделения',
      dataIndex: 'podr',
      key: 'podr',
      ellipsis: true,
      width: 140,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.podr || '').localeCompare(b.podr || ''),
      render: (podr: string | null) => podr || <Text type="secondary">—</Text>,
    },
    {
      title: 'Подразделение',
      dataIndex: 'podr_name_short',
      key: 'podr_name_short',
      ellipsis: true,
      width: 180,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.podr_name_short || '').localeCompare(b.podr_name_short || ''),
    },
    {
      title: 'Адрес',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      width: 200,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.address || '').localeCompare(b.address || ''),
    },
    {
      title: 'Филиал',
      dataIndex: 'fil_code',
      key: 'fil_code',
      width: 200,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.fil_code || 0) - (b.fil_code || 0),
      render: (fil_code: number | null, record: CurrentStructureItem) => {
        if (!fil_code) return <Text type="secondary">— не привязан —</Text>
        return (
          <span>
            <Text type="secondary">{fil_code} — </Text>
            <Text>{record.filial_name || '—'}</Text>
          </span>
        )
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => (a.status || '').localeCompare(b.status || ''),
      render: (status: string | null) => {
        const isActive = status === 'Активно' || status === '1' || status === 'True'
        return (
          <Text type={isActive ? 'success' : 'secondary'}>
            {isActive ? 'Активно' : 'Закрыто'}
          </Text>
        )
      },
    },
    {
      title: 'Период',
      dataIndex: 'last_period_label',
      key: 'last_period_label',
      width: 130,
      sorter: (a: CurrentStructureItem, b: CurrentStructureItem) => a.last_period.localeCompare(b.last_period),
      render: (label: string) => <Text type="secondary">{label}</Text>,
    },
  ]

  // If no periods exist, show message
  if (!hasPeriods) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical">
            <Text>Нет загруженных данных</Text>
            <Text type="secondary">
              Сначала загрузите структуру на вкладке "Загрузка структуры"
            </Text>
          </Space>
        }
      />
    )
  }

  return (
    <div>
      {/* Styles for duplicate rows */}
      <style>{`
        .row-duplicate {
          background-color: #fff7e6 !important;
        }
        .row-duplicate:hover > td {
          background-color: #ffe7ba !important;
        }
      `}</style>

      {/* Header */}
      <Title level={4} style={{ marginBottom: 16 }}>
        Текущая структура отделений
      </Title>

      {/* Stats */}
      <Alert
        message={
          <Space>
            <Text>Всего отделений: {stats.total}</Text>
            <Text type="warning">
              Без филиала: {stats.withoutFilial} ({stats.withoutFilialActive} — Активно, {stats.withoutFilialClosed} — Закрыто)
            </Text>
          </Space>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      {/* Duplicates warning */}
      {data && data.duplicates_count > 0 && (
        <Collapse style={{ marginBottom: 16 }}>
          <Panel
            header={
              <Space>
                <WarningOutlined style={{ color: '#faad14' }} />
                <Text type="warning">
                  Найдены записи с одинаковым кодом ОМС (первые 12 символов, только Активно): {data.duplicates_count} групп
                </Text>
              </Space>
            }
            key="duplicates"
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Проверка уникальности по первым 12 символам поля "Код ОМС" (code_otd) для активных отделений.
              Записи-дубликаты выделены оранжевым цветом в таблице.
            </Text>
            <Space direction="vertical" style={{ width: '100%' }}>
              {data.duplicates.slice(0, 10).map((dup: CodeOtdDuplicate) => (
                <div key={dup.code_otd_12}>
                  <Tag color="orange">{dup.code_otd_12}</Tag>
                  <Text type="secondary"> — {dup.count} записей: </Text>
                  <Text>{dup.departments.join(', ')}</Text>
                  {dup.departments.length < dup.count && (
                    <Text type="secondary"> и ещё {dup.count - dup.departments.length}...</Text>
                  )}
                </div>
              ))}
              {data.duplicates.length > 10 && (
                <Text type="secondary">...и ещё {data.duplicates.length - 10} групп</Text>
              )}
            </Space>
          </Panel>
        </Collapse>
      )}

      {/* Filters */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="Поиск по ID, Код ОМС, названию, адресу"
          value={searchFilter}
          onChange={(e) => {
            setSearchFilter(e.target.value)
            setPage(1)
          }}
          prefix={<SearchOutlined />}
          style={{ width: 350 }}
          allowClear
        />

        <Select
          placeholder="Филиал"
          value={filialFilter}
          onChange={(value) => {
            setFilialFilter(value)
            setOnlyWithoutFilial(false)
            setPage(1)
          }}
          style={{ width: 250 }}
          allowClear
          showSearch
          loading={filialsLoading}
          disabled={onlyWithoutFilial}
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={filials.map((f) => ({
            value: f.fil_code,
            label: `${f.fil_code} - ${f.fil_name}`,
          }))}
        />

        <Checkbox
          checked={onlyWithoutFilial}
          onChange={(e) => {
            setOnlyWithoutFilial(e.target.checked)
            if (e.target.checked) {
              setFilialFilter(undefined)
            }
            setPage(1)
          }}
        >
          Только без филиала
        </Checkbox>

        <Select
          placeholder="Статус"
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value)
            setPage(1)
          }}
          style={{ width: 150 }}
          allowClear
          options={[
            { value: 'Активно', label: 'Активно' },
            { value: 'Закрыто', label: 'Закрыто' },
          ]}
        />

        <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
          Сбросить фильтры
        </Button>
      </Space>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={data?.items || []}
        rowKey="id_otd"
        loading={isLoading}
        scroll={{ x: 1500 }}
        size="small"
        rowClassName={getRowClassName}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Всего ${total} записей`,
          pageSizeOptions: ['50', '100', '200', '500'],
        }}
        onChange={handleTableChange}
      />
    </div>
  )
}
